import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, FormField } from "@promptgatewaysadmin/ui";
import { useAuth, authErrorKey } from "../../auth/AuthContext.js";
import { errorCopy } from "../../copy/errors.js";
import { useToast } from "@promptgatewaysadmin/ui";

/**
 * /reset — request a link.
 *
 * The link is sent by the Firebase client SDK (`sendPasswordResetEmail`), not by Identity:
 * its password-reset endpoint returns 202 and sends nothing. The hook swallows
 * `auth/user-not-found`, so the success state is identical whether or not the address is
 * registered, and its phrasing is conditional: copy that asserts an email WAS sent leaks
 * account existence.
 *
 * The stated "30 minutes" is a promise. It must match the GCIP action-link TTL actually
 * configured — if they differ, change the deployment, not the copy.
 */
export function Reset() {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errKey, setErrKey] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    if (busy || cooldown > 0) return;
    setBusy(true);
    setErrKey(null);
    try {
      await auth.sendPasswordResetEmail(email);
      setSent(true);
      // auth/too-many-requests is a documented response, so the resend carries its own
      // cooldown up front rather than waiting to be refused.
      setCooldown(60);
    } catch (err) {
      const c = (err as { code?: string })?.code;
      setErrKey(typeof c === "string" && c.startsWith("auth/") ? authErrorKey(c) : "error.unavailable");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <>
        <h1>Check your inbox</h1>
        <p>
          If an account exists for <strong>{email}</strong>, a reset link is on its way. It
          expires in 30 minutes.
        </p>
        <Button
          variant="secondary"
          block
          onClick={() => void send()}
          aria-disabled={cooldown > 0 || undefined}
          aria-describedby={cooldown > 0 ? "resend-cooldown" : undefined}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
        </Button>
        {cooldown > 0 && (
          <p id="resend-cooldown" className="pg-disabled-reason">
            You can send another link in {cooldown} seconds.
          </p>
        )}
        <p className="pg-auth__alt">
          <Link to="/login">Back to log in</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <h1>Reset your password</h1>
      <p>We&apos;ll email you a link to set a new one.</p>

      {errKey && (
        <Callout tone="danger" role="alert">
          {errorCopy(errKey).body}
        </Callout>
      )}

      <form onSubmit={send} noValidate>
        <FormField
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Button type="submit" block disabled={busy}>
          {busy ? "Sending…" : "Send reset link"}
        </Button>
      </form>

      <p className="pg-auth__alt">
        <Link to="/login">Back to log in</Link>
      </p>
    </>
  );
}

/**
 * /reset/confirm?oobCode=…
 *
 * The code is verified BEFORE the form renders. A form that collects a new password and
 * then reports the link was dead wastes the user's effort at the worst possible moment.
 *
 * Other sessions are signed out after a reset — the standard defence against session-hijack
 * persistence — and we SAY SO rather than doing it silently.
 */
export function ResetConfirm() {
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const code = params.get("oobCode");

  const [state, setState] = useState<"checking" | "ok" | "expired">("checking");
  const [emailForCode, setEmailForCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errKey, setErrKey] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setState("expired");
      return;
    }
    let cancelled = false;
    void auth
      .verifyPasswordResetCode(code)
      .then((email) => {
        if (cancelled) return;
        setEmailForCode(email);
        setState("ok");
      })
      .catch(() => !cancelled && setState("expired"));
    return () => {
      cancelled = true;
    };
  }, [code, auth]);

  if (state === "checking") return <h1>Checking your link…</h1>;

  if (state === "expired") {
    return (
      <>
        <h1>That link has expired</h1>
        <p>Reset links last 30 minutes and can only be used once. Nothing was changed.</p>
        <Link className="pg-btn pg-btn--primary pg-btn--block" to="/reset">
          Send a new link
        </Link>
      </>
    );
  }

  return (
    <>
      <h1>Set a new password</h1>

      {errKey && (
        <Callout tone="danger" role="alert">
          {errorCopy(errKey).body}
        </Callout>
      )}

      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy || !code) return;
          setBusy(true);
          setErrKey(null);
          try {
            await auth.confirmPasswordReset(code, password);
            await auth.signInWithPassword(emailForCode, password, true);
            toast.show("Password updated. You've been signed out on other devices.");
            navigate("/", { replace: true });
          } catch (err) {
            const c = (err as { code?: string })?.code;
            setErrKey(typeof c === "string" ? authErrorKey(c) : "error.auth.unavailable");
          } finally {
            setBusy(false);
          }
        }}
      >
        {/* Read-only, and from the VERIFIED code — not from a query parameter. */}
        <FormField label="Email" type="email" value={emailForCode} readOnly />
        <FormField
          label="New password"
          type="password"
          required
          autoComplete="new-password"
          help="At least 8 characters, including a number."
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <Button type="submit" block disabled={busy}>
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>

      <p className="pg-auth__note">
        You&apos;ll be signed out on other devices.
      </p>
    </>
  );
}
